import {
  fetchClinicTicketCount,
  fetchClinicTickets,
  SUPPORT_REQUEST_TYPE,
} from '@/entities/support';
import { SUPPORT_REQUEST_STATUS } from '@/shared/config';
import {
  checkIsChangedSorter,
  showMessageError,
  showMessageInfo,
  GETTEXT_ALL_FACILITIES,
  appearDataTable,
  preventAppScroll,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import {
  SUPPORT_REQUEST_STATUS_INVERSE,
  SUPPORT_REQUEST_TYPE_INVERSE,
} from '../../config/supportRequest';
import type { SupportRequestProps, SupportRequestState } from './supportRequest.types';

const statusList = ['All statuses', ...Object.values(SUPPORT_REQUEST_STATUS)];
const categoryList = ['All categories', ...Object.values(SUPPORT_REQUEST_TYPE)];

/**
 * Provides the support request table with its filters, cursor-paged clinic tickets and handlers.
 * Keeps filter state in a ref, fetches tickets on activation, facility change and a 500 ms debounced
 * ticket-id search, toggles a scroll class on the list body, and releases app scroll once loaded.
 *
 * @param props - Props carrying supportFacilities, selectedFacility and isActive.
 * @returns An object carrying navigate, the status and category lists, state, filterObject,
 * isShowFacilityName and the search, dropdown, detail and table-change handlers.
 */
export const useSupportRequest = ({
  supportFacilities,
  selectedFacility,
  isActive,
}: SupportRequestProps) => {
  const navigate = useNavigate();
  const [state, setState] = useMergeState<SupportRequestState>({
    status: statusList[0],
    categories: categoryList[0],
    data: [],
    isLoadedData: false,
    page: 1,
    sizePerPage: 10,
    maxDisplayedData: 10,
    isEndOfData: false,
    clinicTicketCount: undefined,
  });

  const filterObject = useRef<Record<string, any>>({
    status: undefined,
    categories: undefined,
    cursor: undefined,
    sortOrder: 'desc',
    facility: undefined,
    appName: 'Bioflux',
    ticketId: undefined,
  });

  const updateSearchValueTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isShowFacilityName = selectedFacility.value === GETTEXT_ALL_FACILITIES;

  /**
   * Loads clinic support tickets for the request table with cursor paging and facility filtering. Applies the selected facility, resets the cursor unless paging further, appends deduplicated rows while advancing page and maxDisplayedData or fetches the ticket count for the first page, flags end of data, and on failure logs, shows the error and empties the table.
   *
   * @param isFetchMoreByClickingNext - True when the user paged forward, so rows are appended instead of replaced.
   * @param limit - Row count to request, overriding the current page size.
   * @returns Resolves after the ticket rows and paging flags are stored.
   */
  const fetchData = async (isFetchMoreByClickingNext = false, limit?: number) => {
    setState({ isLoadedData: false });
    try {
      filterObject.current.facility = isShowFacilityName ? undefined : selectedFacility.value;

      if (!isFetchMoreByClickingNext) {
        _.assign(filterObject.current, {
          cursor: undefined,
        });
      }

      const queryFilter = { ...filterObject.current };
      const clinicTickets = await fetchClinicTickets(queryFilter, limit || state.sizePerPage);

      const setStateObject = {
        isEndOfData: (clinicTickets ?? []).length < (limit || state.sizePerPage),
        isLoadedData: true,
      };

      if (queryFilter.cursor) {
        const diffData = _.differenceBy(clinicTickets, state.data, 'id');
        const appearedData = appearDataTable(state.data, clinicTickets);
        if (limit) {
          _.assign(setStateObject, {
            data: appearedData,
            maxDisplayedData: isFetchMoreByClickingNext
              ? state.sizePerPage * state.page
              : state.maxDisplayedData,
            isEndOfData:
              (clinicTickets ?? []).length < (limit || state.sizePerPage) || !diffData.length, // *: New data is similar to current data
          });
        } else {
          _.assign(setStateObject, {
            data: appearedData,
            page: isFetchMoreByClickingNext ? state.page + 1 : state.page,
            maxDisplayedData: isFetchMoreByClickingNext
              ? state.maxDisplayedData + state.sizePerPage
              : state.maxDisplayedData,
            isEndOfData:
              (clinicTickets ?? []).length < (limit || state.sizePerPage) || !diffData.length,
          });
        }

        if (_.isEmpty(clinicTickets) && !_.isEmpty(state.data)) {
          showMessageInfo({ content: t`No more requests` });
        }
      } else {
        const clinicTicketCount = await fetchClinicTicketCount(queryFilter);
        _.assign(setStateObject, { data: clinicTickets, clinicTicketCount });
      }

      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch support request: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({
        data: [],
        isLoadedData: true,
      });
    }
  };

  /**
   * Records a keystroke in the support request search field. Merges the named field's new value into the hook state.
   *
   * @param name - State field being edited.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChangeSearchValue = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Opens the detail page of a support request picked from the request table. Navigates to the /support route for the given ticket id.
   *
   * @param ticketId - Identifier of the support ticket to open.
   * @returns Resolves after the navigation is issued.
   */
  const handleClickViewRequestDetail = async (ticketId: any) => {
    navigate(`/support/${ticketId}`);
  };

  /**
   * Applies a category or status dropdown selection to the support request filter. Maps the label back to its API value through the inverse type or status map, writes it onto the filter as an array for categories, clears the cursor, refetches and resets the table to page one.
   *
   * @param name - Filter field the dropdown drives, categories or status.
   * @param value - Selected label to translate into an API value.
   * @returns Nothing.
   */
  const onChangeDropdown = (name: any, value: any) => {
    if (value !== (state as any)[name]) {
      const tempObj =
        name === 'categories' ? SUPPORT_REQUEST_TYPE_INVERSE : SUPPORT_REQUEST_STATUS_INVERSE;
      const tempValue = tempObj[value];
      const formatValue = tempValue ? (name === 'categories' ? [tempValue] : tempValue) : undefined;
      (filterObject.current as any)[name] = formatValue;
      filterObject.current.cursor = undefined;
      fetchData();
      setState({
        [name]: value,
        isLoadedData: false,
        page: 1,
      });
    }
  };

  /**
   * Reacts to sorting and paging changes on the support request table. Refetches from page one when the sort order flipped, otherwise pages forward by setting the cursor to the last row's ticketId and requesting only the missing rows, and merely moves the page marker or reports no more requests when the rows are already loaded.
   *
   * @param pagination - Current page and page size from the table.
   * @param filters - Column filter values from the table.
   * @param sorter - Sort column and order from the table.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const isChangedOrder = checkIsChangedSorter(sorter, filterObject.current, false);
    if (isChangedOrder) {
      const { order } = sorter;
      const sortOrder = !order || order === 'ascend' ? 'asc' : 'desc';
      filterObject.current.sortOrder = sortOrder;
      setState({ page: 1 });
      filterObject.current.cursor = undefined;
      fetchData();
      return;
    }

    const { current, pageSize } = pagination;
    if (current * pageSize > state.data.length && !state.isEndOfData) {
      filterObject.current.cursor = state.data[state.data.length - 1].ticketId;
      _.assign(state, {
        isLoadedData: false,
        page: current,
        sizePerPage: pageSize,
      });
      fetchData(true, pageSize * current - state.data.length);
    } else {
      const ceilLength = Math.ceil(state.data.length / pageSize) * pageSize;
      if (current * pageSize > ceilLength && state.isEndOfData) {
        showMessageInfo({ content: t`No more requests` });
      } else {
        setState({
          page: current,
          sizePerPage: pageSize,
        });
      }
    }
  };

  useEffect(() => {
    const element = document.getElementById('support-request-body');

    /**
     * Marks the scrolled request list so its sticky header can show a shadow. Adds the is-scrolling class once scrollTop is non-zero and removes it when the element is back at the top.
     *
     * @returns Nothing.
     */
    const handleScroll = () => {
      if (!element) return;
      if (element.scrollTop === 0 && element.classList.contains('is-scrolling')) {
        element.classList.remove('is-scrolling');
      } else if (element.scrollTop !== 0 && !element.classList.contains('is-scrolling')) {
        element.classList.add('is-scrolling');
      }
    };

    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => {
        element.removeEventListener('scroll', handleScroll);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (isActive && !state.isLoadedData && !state.isEndOfData) {
      fetchData();
    }
  }, [isActive]);

  useUpdateEffect(() => {
    if (supportFacilities?.length > 0 || facilitySelection.userSupportFacilities()?.length > 0) {
      if (isActive) {
        setState({ page: 1 });
        fetchData();
      } else {
        setState({
          isLoadedData: false,
          isEndOfData: false,
        });
      }
    }
  }, [selectedFacility.value]);

  useUpdateEffect(() => {
    if (state.isLoadedData) {
      preventAppScroll(false);
    }
  }, [state.isLoadedData]);

  useUpdateEffect(() => {
    if (updateSearchValueTimeout.current) {
      clearTimeout(updateSearchValueTimeout.current);
      updateSearchValueTimeout.current = null;
    }
    updateSearchValueTimeout.current = setTimeout(() => {
      filterObject.current.ticketId = state.searchValue ? Number(state.searchValue) : undefined;
      setState({ page: 1 });
      fetchData();
    }, 500);
  }, [state.searchValue]);

  return {
    navigate,
    statusList,
    categoryList,
    state,
    filterObject,
    isShowFacilityName,
    onChangeSearchValue,
    handleClickViewRequestDetail,
    onChangeDropdown,
    handleTableChange,
  };
};
